import React from 'react';
import { useScrollReveal } from '../hooks/useScrollReveal';
import { Database, Cpu, Users, ArrowDown, Zap, Lock } from 'lucide-react';

const layers = [
  {
    num: '01',
    icon: <Database size={24} strokeWidth={1.5} />,
    title: 'Psychological Assessment Engine',
    desc: 'Multi-dimensional assessments covering personality, cognition, emotional intelligence, career aptitude, and learning styles. Validated, structured, and privacy-first.',
    tags: ['IQ Assessment', 'EQ Mapping', 'Learning Style', 'Personality Profile', 'Career Aptitude'],
    color: '#2A9D8F',
    bg: 'rgba(42,157,143,0.07)',
  },
  {
    num: '02',
    icon: <Cpu size={24} strokeWidth={1.5} />,
    title: 'AI Interpretation Engine',
    desc: 'Proprietary AI processes raw assessment data and generates detailed, layered advisory reports — translated into warm, human-readable language for students and parents.',
    tags: ['Natural Language Reports', 'Strength Analysis', 'Gap Identification', 'Career Mapping', 'Parent Summary'],
    color: '#F4A261',
    bg: 'rgba(244,162,97,0.08)',
  },
  {
    num: '03',
    icon: <Users size={24} strokeWidth={1.5} />,
    title: 'Human Counseling Network',
    desc: 'Expert counselors review AI-generated insights and provide personalized guidance sessions. The human layer ensures empathy, context, and actionable next steps.',
    tags: ['Certified Counselors', 'Session Booking', 'Progress Tracking', 'Customized Plans', 'RCI Aligned'],
    color: '#8DA399',
    bg: 'rgba(141,163,153,0.1)',
  },
];

const careerVisuals = [
  {
    title: 'Career Planning',
    image: 'https://images.unsplash.com/photo-1523580494863-6f3031224c94?auto=format&fit=crop&w=640&q=80',
  },
  {
    title: 'Mentor Guidance',
    image: 'https://images.unsplash.com/photo-1552664730-d307ca884978?auto=format&fit=crop&w=640&q=80',
  },
  {
    title: 'College Pathways',
    image: 'https://images.unsplash.com/photo-1523240795612-9a054b0db644?auto=format&fit=crop&w=640&q=80',
  },
];

const PlatformEcosystem = () => {
  const [ref, visible] = useScrollReveal();

  return (
    <section
      ref={ref}
      style={{
      padding: '100px 24px', position: 'relative', overflow: 'hidden',
      background: `linear-gradient(180deg, #F9F9F7 0%, rgba(237,247,245,0.5) 50%, #F9F9F7 100%)`,
    }}
      data-testid="platform-ecosystem-section"
    >
      {/* Decorative background */}
      <div style={{
        position: 'absolute', right: '-200px', top: '50%', transform: 'translateY(-50%)',
        width: '500px', height: '500px',
        background: 'radial-gradient(ellipse, rgba(42,157,143,0.06) 0%, transparent 70%)',
        borderRadius: '50%', pointerEvents: 'none',
      }} />

      <div style={{ maxWidth: '1200px', margin: '0 auto', position: 'relative' }}>
        <div className={`reveal ${visible ? 'visible' : ''}`} style={{ textAlign: 'center', marginBottom: '72px' }}>
          <div className="section-label">Platform Architecture</div>
          <div className="section-divider" />
          <h2 style={{
            fontFamily: 'Manrope', fontWeight: 700, fontSize: 'clamp(30px,3.8vw,46px)',
            color: '#1A202C', margin: '20px 0 16px', letterSpacing: '-0.02em',
          }}>
            Three Layers.<br /><span style={{ fontWeight: 300, color: '#64748B' }}>One Seamless Experience.</span>
          </h2>
          <p style={{ fontSize: '16px', color: '#64748B', maxWidth: '500px', margin: '0 auto', lineHeight: 1.7 }}>
            MindVrik's integrated platform combines scientific rigor, career intelligence, AI interpretation, and human empathy into a single cohesive system.
          </p>
        </div>

        {/* Layers — vertical connected cards */}
        <div style={{ display: 'flex', flexDirection: 'column', gap: '12px', maxWidth: '800px', margin: '0 auto' }}>
          {layers.map((layer, i) => (
            <React.Fragment key={layer.num}>
              <div
                className={`reveal reveal-delay-${i + 1} ${visible ? 'visible' : ''}`}
                data-testid={`ecosystem-layer-${i}`}
                style={{
                  background: 'rgba(255,255,255,0.85)',
                  backdropFilter: 'blur(16px)',
                  border: `1.5px solid ${layer.color}28`,
                  borderRadius: '24px',
                  padding: '36px 40px',
                  boxShadow: '0 4px 24px rgba(0,0,0,0.04)',
                  display: 'grid',
                  gridTemplateColumns: '60px 1fr',
                  gap: '24px',
                  alignItems: 'start',
                  transition: 'all 0.35s ease',
                  cursor: 'default',
                }}
              >
                {/* Icon column */}
                <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '8px' }}>
                  <div style={{
                    width: '60px', height: '60px', borderRadius: '18px',
                    background: layer.bg, border: `1.5px solid ${layer.color}30`,
                    display: 'flex', alignItems: 'center', justifyContent: 'center',
                    color: layer.color,
                  }}>{layer.icon}</div>
                  <div style={{ fontSize: '11px', fontFamily: 'Manrope', fontWeight: 800, color: layer.color, letterSpacing: '0.06em' }}>{layer.num}</div>
                </div>

                {/* Content column */}
                <div>
                  <h3 style={{ fontFamily: 'Manrope', fontWeight: 700, fontSize: '20px', color: '#1A202C', margin: '0 0 10px' }}>{layer.title}</h3>
                  <p style={{ fontFamily: 'Inter', fontSize: '14px', color: '#64748B', lineHeight: 1.7, margin: '0 0 18px' }}>{layer.desc}</p>
                  <div style={{ display: 'flex', flexWrap: 'wrap', gap: '6px' }}>
                    {layer.tags.map(tag => (
                      <span key={tag} style={{
                        fontSize: '11px', fontFamily: 'Inter', fontWeight: 500,
                        color: layer.color, background: layer.bg,
                        padding: '4px 10px', borderRadius: '99px',
                        border: `1px solid ${layer.color}28`
                      }}>{tag}</span>
                    ))}
                  </div>
                </div>
              </div>

              {/* Connector between layers */}
              {i < layers.length - 1 && (
                <div className={`reveal reveal-delay-${i + 2} ${visible ? 'visible' : ''}`} style={{ display: 'flex', justifyContent: 'center' }}>
                  <div style={{
                    display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '4px',
                  }}>
                    <div style={{ width: '1px', height: '20px', background: 'linear-gradient(180deg, rgba(42,157,143,0.3), rgba(42,157,143,0.1))' }} />
                    <div style={{
                      width: '32px', height: '32px', borderRadius: '50%',
                      background: 'rgba(42,157,143,0.1)', border: '1.5px solid rgba(42,157,143,0.2)',
                      display: 'flex', alignItems: 'center', justifyContent: 'center',
                      color: '#2A9D8F',
                    }}>
                      <Zap size={13} strokeWidth={1.5} />
                    </div>
                    <div style={{ width: '1px', height: '20px', background: 'linear-gradient(180deg, rgba(42,157,143,0.1), rgba(42,157,143,0.3))' }} />
                  </div>
                </div>
              )}
            </React.Fragment>
          ))}
        </div>

        <div
          className={`reveal reveal-delay-3 ${visible ? 'visible' : ''}`}
          style={{
            display: 'grid',
            gridTemplateColumns: 'repeat(auto-fit, minmax(180px, 1fr))',
            gap: '14px',
            maxWidth: '800px',
            margin: '34px auto 0',
          }}
        >
          {careerVisuals.map((visual) => (
            <div
              key={visual.title}
              style={{
                height: '126px',
                borderRadius: '18px',
                overflow: 'hidden',
                position: 'relative',
                border: '1px solid rgba(255,255,255,0.8)',
                boxShadow: '0 10px 34px rgba(42,157,143,0.08)',
              }}
            >
              <img
                src={visual.image}
                alt={`${visual.title} visual for MindVrik career guidance`}
                loading="lazy"
                decoding="async"
                style={{ width: '100%', height: '100%', objectFit: 'cover', display: 'block', filter: 'saturate(0.82) brightness(1.04)' }}
              />
              <div style={{ position: 'absolute', inset: 0, background: 'linear-gradient(to top, rgba(26,32,44,0.45), transparent 65%)' }} />
              <div style={{
                position: 'absolute', left: '14px', bottom: '12px',
                color: 'white', fontFamily: 'Manrope', fontWeight: 700, fontSize: '13px',
                letterSpacing: '0.01em',
              }}>
                {visual.title}
              </div>
            </div>
          ))}
        </div>

        {/* Security footer */}
        <div className={`reveal reveal-delay-4 ${visible ? 'visible' : ''}`} style={{ marginTop: '48px', textAlign: 'center' }}>
          <div style={{
            display: 'inline-flex', alignItems: 'center', gap: '8px',
            background: 'rgba(42,157,143,0.06)', border: '1px solid rgba(42,157,143,0.15)',
            borderRadius: '99px', padding: '10px 24px',
          }}>
            <Lock size={14} color="#2A9D8F" />
            <span style={{ fontSize: '13px', fontFamily: 'Inter', fontWeight: 500, color: '#64748B' }}>
              All data is encrypted end-to-end. Advisory, not diagnostic. Ethical by design.
            </span>
          </div>
        </div>
      </div>
    </section>
  );
};

export default PlatformEcosystem;
